import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChangePasswordModal } from "@/components/app/ChangePasswordModal";
import { Plus, Trash2, ShieldCheck, User, KeyRound } from "lucide-react";

export const UsersPanel = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ phone: "", password: "", name: "", role: "staff" });
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (e) {
      toast.error("Kullanıcılar yüklenemedi");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!form.phone.trim() || form.password.length < 4) {
      toast.error("Telefon ve en az 4 karakter şifre gerekli");
      return;
    }
    if (form.phone.replace(/\D/g, "").length !== 10) {
      toast.error("Telefon numarası 10 haneli olmalıdır");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", form);
      setForm({ phone: "", password: "", name: "", role: "staff" });
      load();
      toast.success("Kullanıcı eklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/users/${id}`);
      load();
      toast.success("Kullanıcı silindi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="max-w-2xl">
      {/* Şifre değiştir */}
      <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-4 mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="font-display font-bold">Şifre Değiştir</p>
          <p className="text-xs text-[var(--fb-muted)]">Kendi giriş şifrenizi güncelleyin</p>
        </div>
        <button
          data-testid="change-password-btn"
          onClick={() => setPwOpen(true)}
          className="flex items-center gap-1.5 text-sm font-semibold text-[var(--fb-red)] border border-[var(--fb-red)] hover:bg-red-50 px-4 py-2 rounded-full transition-colors shrink-0"
        >
          <KeyRound className="w-4 h-4" /> Şifre Değiştir
        </button>
      </div>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />

      {/* Yeni kullanıcı ekle */}
      <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-4 mb-4">
        <p className="font-display font-bold mb-3">Yeni Kullanıcı Ekle</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            data-testid="user-form-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ad Soyad"
            className="border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
          <input
            data-testid="user-form-phone"
            value={form.phone}
            inputMode="numeric"
            maxLength={10}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
            placeholder="Telefon (10 haneli)"
            className="border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
          <input
            data-testid="user-form-password"
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Şifre (min 4)"
            className="border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
          <select
            data-testid="user-form-role"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            className="border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)] bg-white"
          >
            <option value="staff">Personel</option>
            <option value="manager">Yönetici</option>
          </select>
        </div>
        <button
          data-testid="add-user-btn"
          onClick={add}
          disabled={saving}
          className="mt-3 flex items-center gap-1 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-full transition-colors active:scale-95"
        >
          <Plus className="w-4 h-4" /> Ekle
        </button>
      </div>

      {/* Kullanıcı listesi */}
      <div className="bg-white rounded-2xl border border-[var(--fb-border)] overflow-hidden divide-y divide-[var(--fb-border)]">
        {users.map((u) => (
          <div
            key={u.id}
            data-testid={`user-row-${u.id}`}
            className="flex items-center gap-3 p-3 sm:p-4 hover:bg-[var(--fb-offwhite)]"
          >
            <div
              className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
                u.role === "manager" ? "bg-red-50 text-[var(--fb-red)]" : "bg-[var(--fb-neutral)] text-[var(--fb-muted)]"
              }`}
            >
              {u.role === "manager" ? <ShieldCheck className="w-5 h-5" /> : <User className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">
                {u.name || "İsimsiz"}
                {currentUser?.id === u.id && (
                  <span className="text-xs text-[var(--fb-muted)] font-normal"> (siz)</span>
                )}
              </p>
              <p className="text-xs text-[var(--fb-muted)]">
                {u.phone} · {u.role === "manager" ? "Yönetici" : "Personel"}
              </p>
            </div>
            {currentUser?.id !== u.id && (
              <button
                data-testid={`delete-user-${u.id}`}
                onClick={() => del(u.id)}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-red-50 text-[var(--fb-red)]"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
